/**
 * Color Legend Component
 *
 * Displays a color key showing what each color represents in the schedule grid.
 * Shows only items currently visible in the grid.
 */

import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import type { ColorCodeMode } from '../utils/colorUtils';
import type { ScheduledClass } from '../../../types/scheduling';

interface ColorLegendProps {
  scheduledClasses: ScheduledClass[];
  colorMode: ColorCodeMode;
  colorMap: Map<string, string>;
  textColor?: string;
  backgroundColor?: string;
}

/**
 * Extract unique items and their labels based on color mode
 */
function getItemsForMode(
  scheduledClasses: ScheduledClass[],
  colorMode: ColorCodeMode
): Array<{ id: string; label: string }> {
  const itemsMap = new Map<string, string>();

  scheduledClasses.forEach(sc => {
    switch (colorMode) {
      case 'styles': {
        const style = sc.class_details.styles?.[0];
        if (style) {
          itemsMap.set(style.id, style.title);
        }
        break;
      }
      case 'teachers': {
        const teacher = sc.teacher_details?.[0];
        if (teacher) {
          itemsMap.set(teacher.id, teacher.teacher_name);
        }
        break;
      }
      case 'rooms': {
        itemsMap.set(sc.room_details.id, sc.room_details.room_name);
        break;
      }
      case 'disciplines': {
        const discipline = sc.class_details.styles?.[0]?.disciplines?.[0];
        if (discipline) {
          // For disciplines, we need to extract the ID
          // Since we're working with IDs in disciplines array, we'll use the ID as both key and label
          itemsMap.set(discipline, discipline);
        }
        break;
      }
      default:
        break;
    }
  });

  // Convert to array and sort alphabetically
  return Array.from(itemsMap.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Get display label for color mode
 */
function getModeLabel(mode: ColorCodeMode): string {
  switch (mode) {
    case 'styles':
      return 'Styles';
    case 'teachers':
      return 'Teachers';
    case 'rooms':
      return 'Rooms';
    case 'disciplines':
      return 'Disciplines';
    default:
      return '';
  }
}

const ColorLegend: React.FC<ColorLegendProps> = ({
  scheduledClasses,
  colorMode,
  colorMap,
  textColor,
  backgroundColor,
}) => {
  const items = getItemsForMode(scheduledClasses, colorMode);

  if (items.length === 0) {
    return null;
  }

  return (
    <Paper
      sx={{
        p: 2,
        mt: 2,
        backgroundColor,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, fontWeight: 'bold', color: textColor }}
      >
        Color Key ({getModeLabel(colorMode)}):
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {items.map(item => {
          const color = colorMap.get(item.id);
          return (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: color,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              />
              <Typography variant="body2" sx={{ color: textColor }}>
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default ColorLegend;
